import { Alert, Button } from "@opowiedzo/ui";
import { useCustom, useCustomMutation, useGetIdentity, useInvalidate, useOne } from "@refinedev/core";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { Badge } from "@/components/Badge";
import {
  BackLink,
  Card,
  CardNote,
  DefinitionList,
  DetailGrid,
  ErrorBox,
  Loading,
  PageHeader,
} from "@/components/Page";
import {
  channelLabel,
  deliveryStatus,
  formatDateTime,
  storyStatus,
  storyVersionLabel,
} from "@/lib/format";
import type { AdminIdentity } from "@/providers/authProvider";
import type { DeliveryRow } from "./deliveries";

/**
 * Statusy, z których backend pozwala ponowić wysyłkę (RESENDABLE w
 * admin_ops/prompts_router.py). Na odpowiedź i na pominięcie narrator już
 * zareagował - przycisk klikalny dla pominiętej wysyłki kończył się 409.
 */
const RESENDABLE = ["queued", "sent", "delivered", "opened", "failed"];

interface DeliveryDetail extends DeliveryRow {
  memoir_id: string;
  opened_at: string | null;
  family_signal_sent_at: string | null;
  created_at: string;
  storyteller_name: string | null;
  storyteller_channel: string | null;
  story_status: string | null;
  story_published_at: string | null;
}

export function DeliveryShowPage() {
  const { id = "" } = useParams();
  const invalidate = useInvalidate();
  const { query } = useOne<DeliveryDetail>({ resource: "prompt-deliveries", id });
  const { data: identity } = useGetIdentity<AdminIdentity | null>();
  const { mutate, mutation } = useCustomMutation();

  const delivery = query.data?.data;
  const mozeDzialac = (identity?.permissions ?? []).includes("deliveries.act");

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!delivery) return null;

  const status = deliveryStatus(delivery.status);
  const moznaPonowic = RESENDABLE.includes(delivery.status);
  const odswiez = () =>
    invalidate({ resource: "prompt-deliveries", invalidates: ["detail", "list"], id });

  const akcja = (sciezka: string) =>
    mutate(
      {
        url: `/prompt-deliveries/${id}/${sciezka}`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: odswiez },
    );

  return (
    <>
      <BackLink to="/wysylki-pytan">Wysyłki pytań</BackLink>
      <PageHeader
        title="Wysyłka pytania"
        description={delivery.memoir_title ?? undefined}
        actions={<Badge tone={status.tone}>{status.label}</Badge>}
      />

      <DetailGrid
        main={
          <>
            <Card title="Pytanie">
              <p className="text-lg text-ink">{delivery.question ?? "-"}</p>
            </Card>

            <Card title="Przebieg">
              <DefinitionList
                rows={[
                  ["Zaplanowane", formatDateTime(delivery.scheduled_at)],
                  ["Wysłane", formatDateTime(delivery.sent_at)],
                  ["Otwarte", formatDateTime(delivery.opened_at)],
                  ["Przypomnienia", String(delivery.reminder_count)],
                  ["Sygnał do rodziny", formatDateTime(delivery.family_signal_sent_at)],
                  ["Powód błędu", delivery.failed_reason ?? "-"],
                ]}
              />
            </Card>

            {delivery.story_id && (
              <StoryPanel
                storyId={delivery.story_id}
                status={delivery.story_status}
                publishedAt={delivery.story_published_at}
                contentAccess={identity?.content_access ?? false}
              />
            )}
          </>
        }
        side={
          <>
            <Card title="Narrator">
              <DefinitionList
                rows={[
                  ["Imię", delivery.storyteller_name ?? "-"],
                  ["Kanał wysyłki", channelLabel(delivery.channel)],
                  ["Kanały narratora", channelLabel(delivery.storyteller_channel)],
                ]}
              />
            </Card>

            <Card title="Akcje">
              {!mozeDzialac ? (
                <p className="text-base text-ink-soft">
                  Twoja rola nie obejmuje akcji na wysyłkach.
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  <p className="text-base text-ink-soft">
                    Ponowna wysyłka tworzy nowy link do odpowiedzi. Poprzedni zostaje ważny -
                    narrator może wrócić do pierwszej wiadomości.
                  </p>
                  {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}
                  <Button
                    variant="primary"
                    disabled={mutation.isPending || !moznaPonowic}
                    onClick={() => akcja("resend")}
                  >
                    Wyślij pytanie ponownie
                  </Button>
                  <Button
                    disabled={mutation.isPending || delivery.status === "answered"}
                    onClick={() => akcja("skip")}
                  >
                    Pomiń to pytanie
                  </Button>
                  {delivery.status === "answered" && (
                    <CardNote>
                      Narrator już odpowiedział - dosyłanie tego samego pytania byłoby natrętne.
                    </CardNote>
                  )}
                  {delivery.status === "skipped" && (
                    <CardNote>
                      Tę wysyłkę pominięto, więc nie da się jej ponowić. Nowe pytanie do tego
                      narratora wyślesz z ekranu{" "}
                      <Link
                        to={`/wyslij-pytanie?memoir_id=${delivery.memoir_id}`}
                        className="text-accent underline underline-offset-3 hover:text-accent-dark"
                      >
                        Wyślij pytanie teraz
                      </Link>
                      .
                    </CardNote>
                  )}
                </div>
              )}
            </Card>
          </>
        }
      />
    </>
  );
}

interface StoryContent extends Record<string, unknown> {
  title: string | null;
  versions: { kind: string; body_md: string; created_at: string }[];
}

function StoryPanel({
  storyId,
  status,
  publishedAt,
  contentAccess,
}: {
  storyId: string;
  status: string | null;
  publishedAt: string | null;
  contentAccess: boolean;
}) {
  // Treść pobieramy dopiero po kliknięciu. Każde takie żądanie zostawia wpis
  // w audycie, więc nie wolno go wysyłać "na wszelki wypadek" przy wejściu.
  const [pokaz, setPokaz] = useState(false);
  const { query } = useCustom<StoryContent>({
    url: `/stories/${storyId}/content`,
    method: "get",
    queryOptions: { enabled: pokaz },
  });

  const stanHistorii = storyStatus(status);

  return (
    <Card title="Powstała historia">
      <DefinitionList
        rows={[
          ["Status", <Badge key="status" tone={stanHistorii.tone}>{stanHistorii.label}</Badge>],
          ["Opublikowana", formatDateTime(publishedAt)],
        ]}
      />

      <div className="mt-3.5">
        {!contentAccess ? (
          <p className="text-base text-ink-soft">
            Treść historii jest zamknięta. Wgląd wymaga osobnego uprawnienia, które superadmin
            włącza na czas konkretnego zgłoszenia.
          </p>
        ) : !pokaz ? (
          <Button onClick={() => setPokaz(true)}>Pokaż treść (zostanie zapisane w audycie)</Button>
        ) : query.isLoading ? (
          <Loading />
        ) : query.error ? (
          <ErrorBox error={query.error} />
        ) : (
          <div className="flex flex-col gap-3.5">
            {query.data?.data.versions.map((version) => (
              <article key={`${version.kind}-${version.created_at}`}>
                <p className="text-sm text-ink-soft">{storyVersionLabel(version.kind)}</p>
                <p className="text-base whitespace-pre-line text-ink">{version.body_md}</p>
              </article>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
